import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DownArrow from "../../assets/svgs/down-arrow.svg";
import sortAsce from "../../assets/svgs/sort-ascending.svg";
import sortDesc from "../../assets/svgs/sort-descending.svg";
import swap from "../../assets/svgs/swap-vertical.svg";
import UpArrow from "../../assets/svgs/up-arrow.svg";
import ActionsMenu from "../../components/ActionsMenu";
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal";
import { Skeleton } from "@chakra-ui/react";
import ContactPagination from "../../components/Pagination/ContactPagination";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import { initialExecutedFilterData } from "../../utils/initialData";
import FellOutContract from "../UnderContract/components/FellOutContract";
import EditUnderContractModal from "../UnderContract/EditUnderContractModal";
import UnderContractArchieveFilter from "./ArchiveFilter/UnderContractArchiveFilter";
import DataAsOfNotice from "../../components/DataAsOfNotice";

// Custom debounce hook
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const UnderContractArchive = () => {
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);
  const [config] = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [listData, setListData] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(10);
  const [selectedItem, setSelectedItem] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [showEditModal, setShowEditModal] = useState(false);
  const [disableDelete, setDisableDelete] = useState(false);
  const [showfelloutcontract, setShowFellOutContract] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [paginationData, setPaginationData] = useState({ totalItems: 0, from: 0, to: 0, totalPages: 0 });
  const queryParams = new URLSearchParams(location.search);
  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : initialExecutedFilterData;
  });
  // ==================== Debounced Search ====================
  const [searchInput, setSearchInput] = useState(queryParams.get('search') ?? '');
  const debouncedSearch = useDebounce(searchInput, 600);
  const [search, setSearch] = useState(queryParams.get('search') ?? '');

  // Trigger API only when >= 3 chars or cleared
  useEffect(() => {
    if (debouncedSearch.length >= 3 || debouncedSearch === '') {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch]);

  // URL sync with debounced search
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (search.trim()) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    navigate({ search: params.toString() }, { replace: true });
  }, [search, navigate, location.search]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const trimmed = newValue.startsWith(" ") ? newValue.trimStart() : newValue;
    setSearchInput(trimmed);
  };

  const listIds = listData?.map((el) => el.id);


  const buildQueryParams = (filters) => {
    let params = `page=${currentPage}&per_page=${itemPerPage}`;

    if (search) {
      const encodedSearch = encodeURIComponent(search);
      params += `&search=${encodedSearch}`;
    }
    const { count, start_count, end_count, fell_count, fell_start_count, fell_end_count, broker } = filters;
    if (count !== undefined) {
      params += `&count=${encodeURIComponent(count)}`;
    }
    if (start_count !== undefined) {
      params += `&start_count=${encodeURIComponent(start_count)}`;
    }
    if (end_count !== undefined) {
      params += `&end_count=${encodeURIComponent(end_count)}`;
    }
    if (fell_end_count !== undefined) {
      params += `&fell_end_count=${encodeURIComponent(fell_end_count)}`;
    }
    if (fell_count !== undefined) {
      params += `&fell_count=${encodeURIComponent(fell_count)}`;
    }
    if (fell_start_count !== undefined) {
      params += `&fell_start_count=${encodeURIComponent(fell_start_count)}`;
    }
    if (broker) {
      params += `&broker=${encodeURIComponent(broker)}`;
    }
    if (sortDirection !== undefined && sortBy !== "id") {
      params += `&sort_direction=${sortDirection}`;
    }
    if (sortBy !== "id" && sortBy !== undefined) {
      params += `&sort_by=${sortBy}`;
    }

    localStorage.setItem("queryParams", params);

    return params;
  };

  const fetchData = (filters = filterData) => {
    const queryParams = buildQueryParams(filters);
    axios
      .get(`${BASE_URL}/archive-listing?${queryParams}&type=under-contract`, config)
      .then((res) => {
        const value = res?.data?.contractlist;
        setListData(value?.data || []);
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        });
        setSelectedItem([]);
        setIsSelectAll(false);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => {
        setLoading(false);
        setSelectedId("");
      });
  };

  useEffect(() => {
    fetchData();
  }, [sortBy, sortDirection]);

  useEffect(() => {
    onSuccess();
  }, [currentPage, itemPerPage, search]);

  const onSuccess = (filters) => {
    setLoading(true);
    if (filters) {
      fetchData(filters);
    } else {
      fetchData(filterData);
    }
  };

  const handleSelectCheck = (id) => {
    if (selectedItem.length === 0) {
      setSelectedItem([id]);
    } else {
      const index = selectedItem?.indexOf(id);
      if (index === -1) {
        setSelectedItem([...selectedItem, id]);
      } else {
        const filterData = selectedItem?.filter((el) => el !== id);
        setSelectedItem(filterData);
      }
    }
  };

  const handleNext = () => {
    if (currentPage !== paginationData.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleDelete = () => {
    const dataToSend = {
      action: "delete",
      type: "under-contract",
      ids: [selectedId],
    };
    setDisableDelete(true);

    axios
      .post(`${BASE_URL}/handle-bulkaction`, dataToSend, config)
      .then((res) => {
        onSuccess();
        setShowDeleteModal(false);
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setDisableDelete(false));
  };

  const toggleRowDetails = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const getDisplayText = (el) => {
    if (el?.lead_type === 0 && el.portfolio !== 1) {
      if (!el?.link?.property_name) {
        return '';
      }
      return (
        <>
          <span>{el.link.property_name}</span>
          <br />
          <span>{el.link.street_address}</span>
          <br />
          <span>{el.link.city}, {el.link.state}</span>
        </>
      );
    }
    if (el?.lead_type === 1 && el.portfolio !== 1) {
      return el?.link?.property_type?.[0]?.type || '';
    }
    return el?.link?.name || '';
  };

  return (
    <BaseLayout>
      <div className="contact-header">
        <p className="head-1 dark-H">
          Under Contract Archive
          {selectedItem?.length > 0 && (
            <span className="body-L ml-2">
              ({selectedItem?.length} <span className="dark-H">{selectedItem?.length === 1 ? "item" : "items"} selected</span>)
            </span>
          )}
        </p>
        <div className="md:flex block gap-6 items-center">
          <div className="flex gap-6 justify-between items-center">
            <UnderContractArchieveFilter
              filterData={filterData}
              onSetFilterData={(value) => {
                setFilterData(value);
              }}
              from="Under Contract"
              onCallApiAgain={(filters) => onSuccess(filters)}
            />
            {/* Updated Search Box */}
            <div className="search-box contacts relative">
              <input
                type="text"
                className="body-S"
                placeholder="Search name"
                value={searchInput}
                onChange={handleInputChange}
                style={{ border: "1px solid #D8D8D8" }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} bg-white rounded-xl border border-gray-200 overflow-hidden mt-4`}>
        <div className="table-info">
            <table className="contact-table two-rows-static">
              <thead>
                <tr className="uppercase body-N dark-M">
                  <th><p>Details</p></th>
                  <th className="green-H">
                    <div className="table-header">
                      Contract Date
                      <img role="button" src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("created_at"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                    </div>
                  </th>
                  <th>
                    <div className="table-header">
                      FELL OUT OF CONTRACT
                      <img role="button" src={sortBy !== "fell_out_of_contract" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("fell_out_of_contract"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                    </div>
                  </th>
                  <th>
                    <div className="table-header">
                      PROPERTY NAME
                      <img role="button" src={sortBy !== "property_name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("property_name"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                    </div>
                  </th>
                  <th>
                    <div className="table-header">
                      Acquisition
                      <img
                        role="button"
                        src={sortBy !== "acquistion" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("acquistion");
                          if (sortDirection === "desc") {
                            setSortDirection("asc");
                          } else {
                            setSortDirection("desc");
                          }
                        }}
                        alt="icon"
                      />
                    </div>
                  </th>
                  <th>
                    <div className="table-header">
                      Disposition
                      <img
                        role="button"
                        src={sortBy !== "disposition" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("disposition");
                          if (sortDirection === "desc") {
                            setSortDirection("asc");
                          } else {
                            setSortDirection("desc");
                          }
                        }}
                        alt="icon"
                      />
                    </div>
                  </th>
                  <th>
                    <div className="table-header">
                      Lease Type
                      <img
                        role="button"
                        src={sortBy !== "lease_type" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("lease_type");
                          if (sortDirection === "desc") {
                            setSortDirection("asc");
                          } else {
                            setSortDirection("desc");
                          }
                        }}
                        alt="icon"
                      />
                    </div>
                  </th>
                  <th>
                    <div className="table-header">
                      Lease Term Remaining
                      <img
                        role="button"
                        src={sortBy !== "lease_remaining" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("lease_remaining");
                          if (sortDirection === "desc") {
                            setSortDirection("asc");
                          } else {
                            setSortDirection("desc");
                          }
                        }}
                        alt="icon"
                      />
                    </div>
                  </th>
                  <th>
                    <div className="table-header">
                      NOI
                      <img role="button" src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("created_at"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                    </div>
                  </th>
                  <th>
                    <div className="table-header">
                      CONTRACT PRICE
                      <img role="button" src={sortBy !== "contract_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("contract_price"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                    </div>
                  </th>
                  <th>
                    <div className="table-header">
                      CONTRACT CAP RATE
                      <img role="button" src={sortBy !== "contract_cap_rate" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("contract_cap_rate"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                    </div>
                  </th>
                  <th>
                    <div className="table-header">
                      % ASKING PRICE
                      <img role="button" src={sortBy !== "asking_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("asking_price"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                    </div>
                  </th>
                  <th>
                    <div className="table-header">
                      GCI TO COMPANY
                      <img role="button" src={sortBy !== "company_gcl" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("company_gcl"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                    </div>
                  </th>
                  <th><div className="table-header">Actions</div></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(10)].map((_, idx) => (
                    <tr key={idx} className="dark-H body-N">
                      <td><Skeleton height="20px" width="20px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="60px" /></td>
                    </tr>
                  ))
                ) : listData?.length === 0 ? (
                  <tr>
                    <td colSpan={14} className="body-N text-center mt-6 p-10 border-0">
                      No Under Contract Found
                    </td>
                  </tr>
                ) : (
                  listData.map((el, idx) => (
                    <React.Fragment key={idx}>
                    <tr className="dark-H body-N">
                      <td>
                        <img role="button" src={expandedRows[el.id] ? UpArrow : DownArrow} alt="toggle" className="sidebar-icons" onClick={() => toggleRowDetails(el.id)} />
                      </td>
                      <td>{moment(el?.created_at).tz('America/New_York').format("MM/DD/YY")}</td>
                      <td>{(el?.fell_out_of_contract)}</td>
                      <td className="head-5 capitalize">
                        <Link
                          to="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (el?.portfolio === 0) navigate(`/property/${el?.executed?.property_id}`);
                            else if (el?.executed?.link?.owner_info_type === "contact") navigate(`/contact/${el?.executed?.link?.owner?.id}`);
                            else if (el?.executed?.link?.owner_info_type === "company") navigate(`/company/${el?.executed?.link?.owner?.id}`);
                          }}
                        >
                          {el?.executed?.link?.property_name}<br />
                          {el?.executed?.link?.street_address}<br />
                          {el?.executed?.link?.city && el?.executed?.link?.state ? `${el.executed.link.city}, ${el.executed.link.state}` : ""}
                        </Link>
                      </td>
                      <td>
                        <Link
                          to={`/contact/${el?.executed?.offer?.buyer_contact_id}`}
                          className="body-N dark-H"
                          onClick={(e) => {
                            e.preventDefault()
                            navigate(`/contact/${el?.executed?.offer?.buyer_contact_id}`)
                          }}
                        >
                          {el?.executed?.offer?.acquistion_name || ""}
                        </Link>
                      </td>
                      <td>
                        <Link
                          to={`/contact/${el?.executed?.offer?.seller_contact_id}`}
                          className="body-N dark-H"
                          onClick={(e) => {
                            e.preventDefault()
                            navigate(`/contact/${el?.executed?.offer?.seller_contact_id}`)
                          }}
                        >
                          {el?.executed?.offer?.disposition_name || ""}
                        </Link>
                      </td>
                      <td className="head-5 capitalize text-center">
                        {el?.executed?.link?.lease_type}
                      </td>
                      <td className="head-5 capitalize text-center">
                        {el?.executed?.lease_term_remaining}
                      </td>
                      <td className="text-center">${el?.executed?.link?.noi_format}</td>
                      <td className="text-center">${el?.contract_price_format}</td>
                      <td className="text-center">{el?.contract_cap_rate_format}%</td>
                      <td className="text-center">{el?.asking_price_format_percentage}%</td>
                      <td className="text-center">${el?.gross_commission?.gci_to_company}</td>
                      <td className="flex gap-3 items-center justify-center">
                        <ActionsMenu
                          from="under-contract"
                          handleEdit={() => {
                            setShowEditModal(true)
                            setSelectedId(el?.id)
                          }}
                          handleDelete={() => {
                            setSelectedId(el?.id)
                            setShowDeleteModal(true)
                          }}
                          // felloutcontractoption={"Fell Out Of Contract"}
                          // handlefelloutcontractOption={() => {
                          //   setShowFellOutContract(true)
                          //   setSelectedId(el?.id)
                          // }}
                        />
                      </td>
                    </tr>

                    {expandedRows[el.id] && (
                      <tr>
                        <td colSpan="11">
                          <div className="expanded-row-content p-4 bg-[rgb(244,244,244)] rounded-md">
                            <div className="">
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-6 items-start">
                                {/* Asking Cap Rate */}
                                <div>
                                  <p className="dark-H head-4 mb-2">Asking Cap Rate</p>
                                  <p className="body-N dark-H">
                                    {el?.link?.asking_cap_rate_format ? `${el?.link?.asking_cap_rate_format}%` : "0"}
                                  </p>
                                </div>

                                {/* Asking Price */}
                                <div>
                                  <p className="dark-H head-4 mb-2">Asking Price</p>
                                  <p className="body-N dark-H">
                                    {el?.link?.asking_price_format ? `$${el?.link?.asking_price_format}` : "0"}
                                  </p>
                                </div>

                                {/* Seller Broker */}
                                <div>
                                  <p className="dark-H head-4 mb-2">Seller Broker</p>
                                  <div className="flex flex-wrap gap-x-2 gap-y-1 body-N dark-H">
                                    {el?.executed?.offer?.seller_broker?.length > 0 ? (
                                      el.executed.offer.seller_broker.map((broker, index) => (
                                        <span key={broker.id || index} className="whitespace-nowrap">
                                          {broker?.first_name || ""} {broker?.last_name || ""}
                                          {index < el.executed.offer.seller_broker.length - 1 ? "," : ""}
                                        </span>
                                      ))
                                    ) : (
                                      <span>—</span>
                                    )}
                                  </div>
                                </div>

                                {/* Seller BDS */}
                                <div>
                                  <p className="dark-H head-4 mb-2">Seller BDS</p>
                                  <p className="body-N dark-H">
                                    {el?.executed?.offer?.seller_bds?.first_name} {el?.executed?.offer?.seller_bds?.last_name || ""}
                                  </p>
                                </div>

                                {/* Buyer Broker */}
                                <div>
                                  <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                  <div className="flex flex-wrap gap-x-2 gap-y-1 body-N dark-H">
                                    {el?.executed?.offer?.buyer_broker?.length > 0 ? (
                                      el.executed.offer.buyer_broker.map((broker, index) => (
                                        <span key={broker.id || index} className="whitespace-nowrap">
                                          {broker?.first_name || ""} {broker?.last_name || ""}
                                          {index < el.executed.offer.buyer_broker.length - 1 ? "," : ""}
                                        </span>
                                      ))
                                    ) : (
                                      <span>—</span>
                                    )}
                                  </div>
                                </div>

                                {/* Buyer BDS */}
                                <div>
                                  <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                  <p className="body-N dark-H">
                                    {el?.executed?.offer?.buyer_bds?.first_name} {el?.executed?.offer?.buyer_bds?.last_name || ""}
                                  </p>
                                </div>
                              </div>


                              <p className="head-3 dark-H my-6 text-center">GROSS COMMISSION INCOME BREAKDOWN</p>

                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="dark-H head-4 mb-2">Seller Broker</p>
                                  <div>
                                    <p className="body-N dark-H">
                                      ${el?.gross_commission?.gci_to_broker_seller?.toLocaleString() || 0}
                                    </p>
                                    {el?.executed?.gcl_broker_seller_share?.length > 0 && (
                                      <div className="mt-1">
                                        {el.executed?.gcl_broker_seller_share.map((item, index) => (
                                          <p key={index} className="body-N dark-H">
                                            {item.name} ({item.share})
                                          </p>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div>
                                  <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                  <div>
                                    <p className="body-N dark-H">
                                      ${el?.gross_commission?.gci_to_broker_buyer?.toLocaleString() || 0}
                                    </p>
                                    {el?.executed?.gcl_broker_buyer_share?.length > 0 && (
                                      <div className="mt-1">
                                        {el.executed?.gcl_broker_buyer_share.map((item, index) => (
                                          <p key={index} className="body-N dark-H">
                                            {item.name} ({item.share})
                                          </p>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>


                                <div>
                                  <p className="dark-H head-4 mb-2">Seller BDS</p>
                                  <p className="body-N  dark-H ">
                                    {el?.gross_commission?.gci_to_broker_representing_seller
                                      ? `$${el.gross_commission?.gci_to_broker_representing_seller.toLocaleString()}`
                                      : "$0"}
                                  </p>
                                </div>

                                <div>
                                  <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                  <p className="body-N  dark-H ">
                                    {el?.gross_commission?.gci_to_broker_representing_buyer
                                      ? `$${el.gross_commission?.gci_to_broker_representing_buyer.toLocaleString()}`
                                      : "$0"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
                )}
              </tbody>
            </table>
        </div>

        <ContactPagination
          from="archive"
          setSelectedItem={setSelectedItem}
          selectedItem={selectedItem}
          paginationData={paginationData}
          handleNext={handleNext}
          handlePrev={handlePrev}
          onSuccess={onSuccess}
          itemPerPage={itemPerPage}
          handleItemPerPageSelect={(value) => {
            setItemPerPage(value);
            setCurrentPage(1);
          }}
        />

        <div className="px-6 pb-2">
          <DataAsOfNotice message="Under contract archive data as of" />
        </div>
      </div>

      <DeleteConfirmationModal showModal={showDeleteModal} disable={disableDelete} onClose={() => setShowDeleteModal(false)} handleDelete={handleDelete} />
      <FellOutContract from="undercontact" showModal={showfelloutcontract} id={selectedId} onClose={() => setShowFellOutContract(false)} onCallApi={onSuccess} />
      <EditUnderContractModal showModal={showEditModal} onClose={() => setShowEditModal(false)} id={selectedId} onCallApi={() => { setSelectedId(""); onSuccess(); }} />
    </BaseLayout>
  );
};

export default UnderContractArchive;